import axios from 'axios';
import { semanticSearch } from './geminiService';

const BASE_URL = 'https://api.github.com';

/**
 * 验证GitHub个人访问令牌
 * @param {string} token - GitHub个人访问令牌
 * @returns {Promise<boolean>} - 返回令牌是否有效的Promise
 */
export const validateToken = async (token) => {
  if (!token) return false;
  
  try {
    const response = await axios.get(`${BASE_URL}/user`, {
      headers: {
        Authorization: `token ${token}`,
        Accept: 'application/vnd.github.v3+json',
      },
    });
    return response.status === 200;
  } catch (error) {
    console.error('令牌验证失败:', error);
    return false;
  }
};

/**
 * 获取仓库的README内容
 * @param {string} owner - 仓库所有者
 * @param {string} repo - 仓库名称
 * @param {string} token - GitHub个人访问令牌（可选）
 * @returns {Promise<string>} - 返回README内容的Promise
 */
export const getRepoReadme = async (owner, repo, token = '') => {
  try {
    const headers = {
      Accept: 'application/vnd.github.v3.raw+json',
    };
    
    // 如果提供了令牌，添加到请求头
    if (token) {
      headers.Authorization = `token ${token}`;
    }
    
    const response = await axios.get(`${BASE_URL}/repos/${owner}/${repo}/readme`, {
      headers,
      // 添加超时设置，避免请求过长
      timeout: 5000,
    });
    
    return response.data;
  } catch (error) {
    // 更详细地记录错误信息
    if (error.response) {
      // 服务器返回了错误状态码
      const status = error.response.status;
      if (status === 403) {
        console.warn(`获取 ${owner}/${repo} 的README失败: 权限不足或API限制 (403)`);
      } else if (status === 404) {
        console.log(`${owner}/${repo} 没有README文件 (404)`);
      } else {
        console.log(`获取 ${owner}/${repo} 的README失败: HTTP状态码 ${status}`);
      }
    } else if (error.request) {
      // 请求已发送但没有收到响应
      console.warn(`获取 ${owner}/${repo} 的README失败: 无响应或超时`);
    } else {
      // 请求设置时出错
      console.error(`获取 ${owner}/${repo} 的README失败: ${error.message}`);
    }
    return ''; // 如果获取失败，返回空字符串
  }
};

/**
 * 获取用户Star的仓库列表（单页）
 * @param {string} username - GitHub用户名
 * @param {number} page - 页码
 * @param {number} perPage - 每页数量
 * @param {string} token - GitHub个人访问令牌（可选）
 * @returns {Promise} - 返回包含仓库数据的Promise
 */
const fetchStarredReposPage = async (username, page = 1, perPage = 100, token = '') => {
  try {
    const headers = {
      Accept: 'application/vnd.github.v3.star+json',
    };
    
    // 如果提供了令牌，添加到请求头
    if (token) {
      headers.Authorization = `token ${token}`;
    }
    
    const response = await axios.get(`${BASE_URL}/users/${username}/starred`, {
      params: {
        page,
        per_page: perPage,
      },
      headers,
    });
    
    // 处理响应数据，提取starred_at信息
    const processedData = response.data.map(item => ({
      ...item.repo,
      starred_at: item.starred_at
    }));
    
    return {
      data: processedData,
      hasNextPage: response.data.length === perPage,
      totalCount: parseInt(response.headers['x-total-count'] || '0', 10) || response.data.length,
      rateLimit: {
        limit: parseInt(response.headers['x-ratelimit-limit'] || '0', 10),
        remaining: parseInt(response.headers['x-ratelimit-remaining'] || '0', 10),
        reset: parseInt(response.headers['x-ratelimit-reset'] || '0', 10),
      }
    };
  } catch (error) {
    // 处理特定的API错误
    if (error.response) {
      if (error.response.status === 401) {
        throw new Error('GitHub API认证失败：无效的访问令牌');
      } else if (error.response.status === 403) {
        throw new Error('GitHub API请求受限：已达到API请求限制');
      } else if (error.response.status === 404) {
        throw new Error(`GitHub用户 "${username}" 不存在`);
      }
    }
    
    console.error('获取Star仓库页面失败:', error);
    throw error;
  }
};

/**
 * 递归获取用户所有Star的仓库列表
 * @param {string} username - GitHub用户名
 * @param {number} perPage - 每页数量
 * @param {function} onProgress - 进度回调函数，参数为(当前加载的仓库数, 总仓库数, 当前页码)
 * @param {string} token - GitHub个人访问令牌（可选）
 * @returns {Promise} - 返回包含所有仓库数据的Promise
 */
export const getStarredRepos = async (username, perPage = 100, onProgress = null, token = '') => {
  try {
    let allRepos = [];
    let currentPage = 1;
    let hasNextPage = true;
    let totalCount = 0;
    
    // 获取第一页，以确定总数
    const firstPage = await fetchStarredReposPage(username, currentPage, perPage, token);
    allRepos = firstPage.data;
    hasNextPage = firstPage.hasNextPage;
    totalCount = firstPage.totalCount || allRepos.length;
    
    if (onProgress) {
      onProgress(allRepos.length, totalCount, currentPage);
    }
    
    // 获取剩余页面
    while (hasNextPage) {
      currentPage++;
      try {
        const nextPage = await fetchStarredReposPage(username, currentPage, perPage, token);
        allRepos = [...allRepos, ...nextPage.data];
        hasNextPage = nextPage.hasNextPage;
        
        if (onProgress) {
          onProgress(allRepos.length, totalCount, currentPage);
        }
      } catch (error) {
        console.error(`获取第 ${currentPage} 页失败:`, error);
        // 如果获取某页失败，尝试继续获取下一页
        if (error.response && error.response.status === 403) {
          // 如果是API限制，等待一分钟后重试
          console.log('API请求受限，等待60秒后重试...');
          await new Promise(resolve => setTimeout(resolve, 60000));
          currentPage--; // 重试当前页
        } else {
          // 其他错误则停止获取
          hasNextPage = false;
        }
      }
    }
    
    return allRepos;
  } catch (error) {
    console.error('获取Star仓库列表失败:', error);
    throw error;
  }
};

/**
 * 获取用户Star的仓库总数
 * @param {string} username - GitHub用户名
 * @param {string} token - GitHub个人访问令牌（可选）
 * @returns {Promise<number>} - 返回包含仓库总数的Promise
 */
export const getStarredReposCount = async (username, token = '') => {
  try {
    const headers = {
      Accept: 'application/vnd.github.v3+json',
    };
    
    // 如果提供了令牌，添加到请求头
    if (token) {
      headers.Authorization = `token ${token}`;
    }
    
    const response = await axios.get(`${BASE_URL}/users/${username}`, { headers });
    return response.data.starred_repos || response.data.public_repos || 0;
  } catch (error) {
    console.error('获取Star仓库总数失败:', error);
    throw error;
  }
};

/**
 * 过滤仓库列表
 * @param {Array} repos - 仓库列表
 * @param {string} keyword - 搜索关键词
 * @param {boolean} useSemanticSearch - 是否使用语义搜索
 * @param {string} apiKey - Gemini API密钥
 * @param {number} threshold - 相似度阈值
 * @param {string} token - GitHub个人访问令牌（可选）
 * @param {boolean} includeReadme - 是否包含README内容
 * @returns {Promise<Array>} - 返回过滤后的仓库列表
 */
export const filterRepos = async (repos, keyword, useSemanticSearch = false, apiKey = '', threshold = 0.7, token = '', includeReadme = false) => {
  if (!keyword) return repos.map(repo => ({ repo, similarity: 1 }));
  
  try {
    // 复制原始仓库列表
    const reposWithReadme = [...repos];
    
    // 如果需要包含README内容，则获取README
    if (includeReadme) {
      // 为每个仓库获取README内容（批量处理，避免API限制）
      let batchSize;
      if (token){
        batchSize = 50; // 批量大小，避免同时发送过多请求
      }else{
        batchSize = 10; // 批量大小，避免同时发送过多请求
      }
      
      // 创建进度事件
      window.dispatchEvent(new CustomEvent('readmeProgress', {
        detail: { total: repos.length, current: 0, phase: 'preparing' }
      }));
      
      // 批量获取README，不限制数量
      for (let i = 0; i < repos.length; i += batchSize) {
        const batch = repos.slice(i, i + batchSize);
        
        // 使用Promise.allSettled代替Promise.all，确保一个失败不会影响其他请求
        const results = await Promise.allSettled(batch.map(async (repo) => {
          try {
            if (repo.owner && repo.owner.login && repo.name) {
              const readme = await getRepoReadme(repo.owner.login, repo.name, token);
              return { index: i + batch.indexOf(repo), readme };
            }
            return { index: i + batch.indexOf(repo), readme: '' };
          } catch (error) {
            console.error(`获取 ${repo.full_name} 的README失败:`, error);
            return { index: i + batch.indexOf(repo), readme: '' };
          }
        }));
        
        // 处理结果
        results.forEach(result => {
          if (result.status === 'fulfilled') {
            const { index, readme } = result.value;
            if (index < reposWithReadme.length) {
              reposWithReadme[index].readme = readme;
            }
          }
          // 如果是rejected，对应的仓库README保持为空
        });
        
        // 更新进度
        const currentProgress = Math.min(i + batch.length, repos.length);
        window.dispatchEvent(new CustomEvent('readmeProgress', {
          detail: { 
            total: repos.length, 
            current: currentProgress, 
            phase: 'processing',
            currentRepo: batch.length > 0 ? batch[batch.length - 1].name : ''
          }
        }));
        
        // 添加延迟，避免API限制
        if (i + batchSize < repos.length) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // 增加延迟时间
        }
      }
      
      // 完成README获取
      window.dispatchEvent(new CustomEvent('readmeProgress', {
        detail: { total: repos.length, current: repos.length, phase: 'complete' }
      }));
    } else {
      // 如果不包含README，则清除进度显示
      window.dispatchEvent(new CustomEvent('readmeProgress', {
        detail: { phase: null }
      }));
    }
    
    // 如果启用了语义搜索，使用Gemini的嵌入模型进行语义搜索
    if (useSemanticSearch) {
      if (!apiKey) {
        throw new Error('未提供Gemini API密钥，无法使用语义搜索功能');
      }
      // 获取所有计算过相似度的结果，不进行阈值过滤
      return await semanticSearch(reposWithReadme, keyword, apiKey, 0, includeReadme);
    }
    
    // 否则使用基本的关键词过滤
    const lowerKeyword = keyword.toLowerCase();
    
    // 对于关键词搜索，我们使用一个简单的相似度计算：匹配的字段数量 / 总字段数量
    return reposWithReadme.map(repo => {
      // 根据是否包含README决定匹配字段
      const matchFields = [
        repo.name.toLowerCase().includes(lowerKeyword),
        repo.description && repo.description.toLowerCase().includes(lowerKeyword),
        repo.language && repo.language.toLowerCase().includes(lowerKeyword),
        repo.owner?.login && repo.owner.login.toLowerCase().includes(lowerKeyword),
        repo.topics && repo.topics.some(topic => topic.toLowerCase().includes(lowerKeyword))
      ];
      
      // 如果包含README，添加README匹配
      if (includeReadme && repo.readme) {
        matchFields.push(repo.readme.toLowerCase().includes(lowerKeyword));
      }
      
      const matchCount = matchFields.filter(Boolean).length;
      const totalFields = matchFields.length;
      const similarity = totalFields > 0 ? matchCount / totalFields : 0;
      
      return {
        repo,
        similarity: similarity
      };
    }).filter(item => item.similarity > 0) // 只返回相似度大于0的仓库
      .sort((a, b) => b.similarity - a.similarity);
  } catch (error) {
    console.error('过滤仓库失败:', error);
    // 如果语义搜索失败，回退到基本的关键词过滤
    const lowerKeyword = keyword.toLowerCase();
    return repos.filter(repo => 
      repo.name.toLowerCase().includes(lowerKeyword) || 
      (repo.description && repo.description.toLowerCase().includes(lowerKeyword)) ||
      (repo.language && repo.language.toLowerCase().includes(lowerKeyword)) ||
      (repo.owner && repo.owner.login && repo.owner.login.toLowerCase().includes(lowerKeyword)) ||
      (repo.topics && repo.topics.some(topic => topic.toLowerCase().includes(lowerKeyword))) ||
      (includeReadme && repo.readme && repo.readme.toLowerCase().includes(lowerKeyword)) // 添加README内容匹配
    ).map(repo => ({ repo, similarity: 1 }));
  }
}; 